import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ABTestMetricsTabContent } from '@/components/ab-test/metrics/tabs/ABTestMetricsTabContent';
import { ABTestRetentionTabContent } from '@/components/ab-test/metrics/tabs/ABTestRetentionTabContent';
import { VideoTestData } from '@/components/ab-test/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { PeriodPicker } from '@/components/ui/period-picker';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { buildPresetRange } from '@/utils/period-presets';
import { useABTestStore } from '@/store/ab-test/abTestStore';
import { Loader2 } from 'lucide-react';


export default function ABTestMetrics() {
  useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { metrics, isLoading, fetchTestMetrics, tests } = useABTestStore();

  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    buildPresetRange('last7Days', new Date())
  );

  // Buscar o teste atual
  const currentTest = tests.find(t => t.id === id);

  // Buscar métricas quando o componente montar ou a data mudar
  useEffect(() => {
    if (id && dateRange?.from && dateRange?.to) {
      const inicio = format(dateRange.from, 'yyyy-MM-dd');
      const fim = format(dateRange.to, 'yyyy-MM-dd');

      fetchTestMetrics(id, inicio, fim);
    }
  }, [id, dateRange, fetchTestMetrics]);

  // Mapear métricas da API para o formato do componente
  const mappedVideos: VideoTestData[] = metrics.map((item: any) => {
    const testAb = item.testAb || {};
    const metric = item.metric || {};
    const totais = metric.totais || {};

    // Calcular play rate (taxa de reprodução) - garantir que seja um número válido
    const visualizacoes = Number(totais.visualizacoes) || 0;
    const plays = Number(totais.plays) || 0;
    const playRate = visualizacoes > 0
      ? (plays / visualizacoes) * 100
      : 0;

    // Garantir que todos os valores sejam números válidos
    const engajamento = Number(totais.engajamento) || 0;

    return {
      id: testAb.id_video || '',
      name: testAb.nomeVideo || translateInterfaceText("Vídeo sem nome"),
      thumbnail: testAb.frame || '',
      trafficPercentage: Number(testAb.trafego) || 0,
      views: Number(totais.visualizacoes) || 0,
      viewsUnique: Number(totais.visualizacoesUnicas) || 0,
      plays: Number(totais.plays) || 0,
      playsUnique: Number(totais.playsUnicos) || 0,
      playRate: isFinite(playRate) ? playRate : 0,
      avgEngagement: isFinite(engajamento) ? engajamento : 0,
      // Raiz do retorno de MetricService.execute (não em `totais`): um item por
      // pitch do vídeo, com a retenção de cada um (null = sem dado).
      pitches: Array.isArray(metric.pitches) ? metric.pitches : [],
      // Alvo do PUT ao configurar o pitch pela tabela (no A/B é o próprio vídeo).
      pitchVideoId: metric.pitchVideoId ?? null,
      videoDuration: metric.videoDuration ?? null,
      buttonClicks: Number(totais.clicks) || 0,
      conversions: 0, // Não utilizado
      conversionRate: 0, // Não utilizado
      revenue: 0, // Não utilizado
      platforms: [], // Não utilizado
      isWinning: false, // Será calculado depois
    };
  });

  // Determinar o vencedor baseado no play rate
  if (mappedVideos.length > 0) {
    const maxPlayRate = Math.max(...mappedVideos.map(v => v.playRate));
    mappedVideos.forEach(video => {
      if (video.playRate === maxPlayRate && maxPlayRate > 0) {
        video.isWinning = true;
      }
    });
  }

  // Encontrar a duração máxima entre os vídeos do teste
  const maxDuration = metrics.reduce((max: number, item: any) => {
    const duration = item.metric?.totais?.duracaoVideo || 0;
    return Math.max(max, duration);
  }, 0);

  // Processar dados de retenção
  const retentionData = (() => {
    if (!metrics || metrics.length === 0) return [];

    // Pegar todos os timestamps únicos de todos os vídeos
    const allTimestamps = new Set<string>();
    metrics.forEach((item: any) => {
      const retencao = item.metric?.retencao || {};
      Object.keys(retencao).forEach(time => allTimestamps.add(time));
    });

    // Converter para array e ordenar
    const sortedTimestamps = Array.from(allTimestamps).sort();

    // Converter timestamp "MM:SS" para segundos
    const parseTimestamp = (timestamp: string): number => {
      const [minutes, seconds] = timestamp.split(':').map(Number);
      return minutes * 60 + seconds;
    };

    // Armazenar o último valor de retenção conhecido para cada vídeo
    const lastKnownValues: { [videoId: string]: number } = {};

    // Criar dados de retenção no formato do gráfico
    return sortedTimestamps.map((timestamp) => {
      const timeInSeconds = parseTimestamp(timestamp);

      const dataPoint: any = {
        time: timeInSeconds,
      };

      // Adicionar dados de cada vídeo
      metrics.forEach((item: any) => {
        const retencao = item.metric?.retencao || {};
        const videoId = item.testAb?.id_video;

        if (!videoId) return;

        // Se o timestamp existe para este vídeo, usa o valor real
        if (retencao[timestamp] && retencao[timestamp].percent !== undefined) {
          const retentionValue = retencao[timestamp].percent;
          dataPoint[videoId] = retentionValue;
          lastKnownValues[videoId] = retentionValue;
        }
        // Se não existe mas já temos um valor anterior, mantém o último
        else if (lastKnownValues[videoId] !== undefined) {
          dataPoint[videoId] = lastKnownValues[videoId];
        }
        // Se não existe e não temos valor anterior, não adiciona nada
        // (deixa undefined para que o gráfico não mostre ponto)
      });

      return dataPoint;
    });
  })();

  const testName = currentTest?.name || "Carregando...";
  const testStatus: 'active' | 'paused' | 'finished' = currentTest?.is_active ? 'active' : 'paused';
  const numberOfVideos = currentTest?.videos?.length || mappedVideos.length;

  const retentionVideos = mappedVideos.map((video) => ({
    id: video.id,
    name: video.name
  }));

  const winnerVideo = mappedVideos.find(v => v.isWinning);
  const winnerVideoName = winnerVideo?.name;

  const statusLabels = {
    active: 'Ativo',
    paused: 'Pausado',
    finished: 'Finalizado'
  };

  const statusStyles = {
    active: {
      background: 'rgba(45, 108, 246, 0.1)',
      border: '1px solid #2D6CF6',
      color: '#2D6CF6',
    },
    paused: {
      background: 'rgba(230, 81, 0, 0.1)',
      border: '1px solid #E65100',
      color: '#E65100',
    },
    finished: {
      background: 'rgba(107, 114, 128, 0.1)',
      border: '1px solid #6B7280',
      color: '#6B7280',
    }
  };

  if (isLoading) {
    return (
      <Layout fixedSidebar>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1D4FFF]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout fixedSidebar>
      <div className="flex-1 flex flex-col bg-background overflow-hidden">
        {/* Premium Header */}
        <div className="flex-shrink-0 bg-transparent">
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 md:pt-4 pb-4 md:pb-5">
            {/* Título */}
            <h1 className="text-[20px] md:text-[22px] font-semibold text-[#1E2330] tracking-tight leading-[1.2]">
              {translateInterfaceText("Desempenho do Seu Teste A/B")}</h1>
          </div>
          <div className="hidden lg:block h-px w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }} />

          {/* Breadcrumb */}
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/ab-test/list')}
                className="text-[14px] font-medium hover:underline transition-colors"
                style={{ color: '#404040' }}
              >
                {translateInterfaceText("Teste:")}{" "}{testName}
              </button>
              <span className="text-[14px]" style={{ color: '#9E9E9E' }}>{">"}</span>
              <span className="text-[14px]" style={{ color: '#9E9E9E' }}>{numberOfVideos} {" "}{translateInterfaceText("vídeos")}</span>
              <span className="text-[14px]" style={{ color: '#9E9E9E' }}>{">"}</span>
              <Badge
                className="text-xs font-bold px-2.5 py-1 rounded-md"
                style={statusStyles.active}
              >
                {statusLabels.active}
              </Badge>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#C5C5C5 transparent' }}>
          <div className="px-4 lg:px-[60px] py-8 max-w-[1320px] mx-auto">
            <Tabs defaultValue="retention" className="w-full">
              <div className="flex items-center justify-between mb-7">
                <TabsList>
                  <TabsTrigger value="retention">
                    {translateInterfaceText("Retenção")}</TabsTrigger>
                  <TabsTrigger value="metrics">
                    {translateInterfaceText("Métricas")}</TabsTrigger>
                </TabsList>

                <PeriodPicker
                  value={dateRange}
                  onChange={setDateRange}
                  align="end"
                  className="md:min-w-[240px]"
                />
              </div>

              <TabsContent value="retention" className="mt-7 animate-fade-in">
                {mappedVideos.length > 0 ? (
                  <ABTestRetentionTabContent
                    videos={mappedVideos}
                    retentionData={retentionData}
                    winnerVideoId={winnerVideo?.id}
                    winnerVideoName={winnerVideoName}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <p className="text-gray-500">{translateInterfaceText("Nenhuma métrica disponível para o período selecionado.")}</p>
                    <p className="text-sm text-gray-400 mt-2">{translateInterfaceText("Tente selecionar um intervalo de datas diferente.")}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="metrics" className="mt-7 animate-fade-in">
                {mappedVideos.length > 0 ? (
                  <ABTestMetricsTabContent
                    videos={mappedVideos}
                    isEditable={false}
                    onPitchSaved={() => {
                      // Recarrega as métricas com o mesmo período: a retenção no
                      // pitch é recalculada no backend a partir do novo tempo.
                      if (id && dateRange?.from && dateRange?.to) {
                        fetchTestMetrics(
                          id,
                          format(dateRange.from, 'yyyy-MM-dd'),
                          format(dateRange.to, 'yyyy-MM-dd'),
                        );
                      }
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <p className="text-gray-500">{translateInterfaceText("Nenhuma métrica disponível para o período selecionado.")}</p>
                    <p className="text-sm text-gray-400 mt-2">{translateInterfaceText("Tente selecionar um intervalo de datas diferente.")}</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </Layout>
  );
}
